import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  Box,
  makeStyles,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Divider
} from '@material-ui/core';
import {
  VpnKey as VpnKeyIcon,
  ExitToApp as ExitToAppIcon
} from '@material-ui/icons';
import { useLocation, useHistory } from 'react-router-dom';
import { authenticationService } from '../services';

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: '#37474f',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    zIndex: theme.zIndex.drawer - 1,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: '56px',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  pageTitle: {
    fontWeight: 600,
    fontSize: '1.1rem',
    color: '#fff',
  },
  userSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '8px',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.1)',
    },
  },
  userName: {
    color: '#fff',
    fontSize: '0.95rem',
    fontWeight: 500,
  },
  avatar: {
    width: 36,
    height: 36,
    backgroundColor: '#fff',
    color: '#37474f',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
}));

const TopBar = ({ onLogout }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const history = useHistory();

  // Estado para el menú de usuario
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [cerrandoSesion, setCerrandoSesion] = useState(false);
  
  // Estado para el diálogo de cambiar contraseña
  const [cambiarPasswordDialogOpen, setCambiarPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    passwordActual: '',
    passwordNueva: '',
    confirmarPassword: ''
  });
  const [cambiandoPassword, setCambiandoPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Obtener información del usuario desde localStorage (session)
  const getUserInfo = () => {
    try {
      const sessionStr = localStorage.getItem('session');
      if (sessionStr) {
        const session = JSON.parse(sessionStr);
        // El objeto session puede tener la info del usuario directamente o en session.user
        const user = session.user || session;
        return {
          nombre: user.nombres || user.nombre || user.firstName || '',
          apellido: user.apellidos || user.apellido || user.lastName || '',
          username: user.username || user.email || user.sub || ''
        };
      }
    } catch (e) {
      console.error('Error parsing user info:', e);
    }
    return { nombre: '', apellido: '', username: '' };
  };

  const userInfo = getUserInfo();
  const nombreCompleto = userInfo.nombre && userInfo.apellido 
    ? `${userInfo.nombre} ${userInfo.apellido}`
    : userInfo.username || 'Usuario';

  const getInitials = () => {
    if (userInfo.nombre && userInfo.apellido) {
      return `${userInfo.nombre.charAt(0)}${userInfo.apellido.charAt(0)}`.toUpperCase();
    }
    if (userInfo.username) {
      return userInfo.username.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Obtener título de la página según la ruta
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/solicitudes') return 'Solicitudes';
    if (path.startsWith('/solicitudes/')) return 'Detalle de Solicitud';
    if (path === '/configuracion') return 'Configuración';
    if (path === '/seguridad') return 'Seguridad';
    return 'Back Office';
  };

  // Handlers del menú de usuario
  const handleUserMenuClick = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  // Handlers del diálogo de cambiar contraseña
  const handleOpenCambiarPassword = () => {
    handleUserMenuClose();
    setPasswordForm({
      passwordActual: '',
      passwordNueva: '',
      confirmarPassword: ''
    });
    setError('');
    setSuccess('');
    setCambiarPasswordDialogOpen(true);
  };

  const handleCerrarSesion = async () => {
    try {
      setCerrandoSesion(true);
      handleUserMenuClose();
      await authenticationService.logout();
      if (onLogout) {
        onLogout();
      }
      history.push('/login');
    } catch (err) {
      console.error('Error cerrando sesión:', err);
    } finally {
      setCerrandoSesion(false);
    }
  };

  const handleCloseCambiarPassword = () => {
    setCambiarPasswordDialogOpen(false);
    setPasswordForm({
      passwordActual: '',
      passwordNueva: '',
      confirmarPassword: ''
    });
    setError('');
    setSuccess('');
  };

  const handlePasswordFormChange = (field) => (event) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError('');
  };

  const handleCambiarPassword = async () => {
    // Validaciones
    if (!passwordForm.passwordActual) {
      setError('Ingrese la contraseña actual');
      return;
    }
    if (!passwordForm.passwordNueva) {
      setError('Ingrese la nueva contraseña');
      return;
    }
    if (passwordForm.passwordNueva.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (passwordForm.passwordNueva !== passwordForm.confirmarPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    try {
      setCambiandoPassword(true);
      setError('');
      
      await authenticationService.cambiarPassword(
        passwordForm.passwordActual,
        passwordForm.passwordNueva
      );
      
      setSuccess('Contraseña cambiada correctamente');
      setTimeout(() => {
        handleCloseCambiarPassword();
      }, 1500);
    } catch (err) {
      console.error('Error cambiando contraseña:', err);
      setError(err.message || 'Error al cambiar la contraseña');
    } finally {
      setCambiandoPassword(false);
    }
  };

  const isPasswordFormValid = () => {
    return (
      passwordForm.passwordActual.trim() !== '' &&
      passwordForm.passwordNueva.trim() !== '' &&
      passwordForm.confirmarPassword.trim() !== '' &&
      passwordForm.passwordNueva === passwordForm.confirmarPassword
    );
  };

  // No mostrar en móvil (ya tiene su propia AppBar)
  if (isMobile) {
    return null;
  }

  return (
    <>
      <AppBar position="static" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <Typography className={classes.pageTitle}>
            {getPageTitle()}
          </Typography>
          
          <Box 
            className={classes.userSection}
            onClick={handleUserMenuClick}
          >
            <Typography className={classes.userName}>
              {nombreCompleto}
            </Typography>
            <Avatar className={classes.avatar}>
              {getInitials()}
            </Avatar>
          </Box>
          
          <Menu
            anchorEl={userMenuAnchorEl}
            keepMounted
            open={Boolean(userMenuAnchorEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            getContentAnchorEl={null}
          >
            <MenuItem onClick={handleOpenCambiarPassword}>
              <VpnKeyIcon fontSize="small" style={{ marginRight: '0.5rem' }} />
              Cambiar Contraseña
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleCerrarSesion} disabled={cerrandoSesion}>
              <ExitToAppIcon fontSize="small" style={{ marginRight: '0.5rem' }} />
              {cerrandoSesion ? 'Cerrando...' : 'Cerrar Sesión'}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Diálogo para cambiar contraseña */}
      <Dialog 
        open={cambiarPasswordDialogOpen} 
        onClose={handleCloseCambiarPassword}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cambiar Contraseña</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            {error && (
              <Box p={2} mb={2} style={{ backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
                <Typography variant="body2">{error}</Typography>
              </Box>
            )}
            {success && (
              <Box p={2} mb={2} style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px' }}>
                <Typography variant="body2">{success}</Typography>
              </Box>
            )}
            <TextField
              fullWidth
              label="Contraseña Actual"
              variant="outlined"
              type="password"
              value={passwordForm.passwordActual}
              onChange={handlePasswordFormChange('passwordActual')}
              required
              style={{ marginBottom: '1rem' }}
              disabled={cambiandoPassword}
            />
            <TextField
              fullWidth
              label="Nueva Contraseña"
              variant="outlined"
              type="password"
              value={passwordForm.passwordNueva}
              onChange={handlePasswordFormChange('passwordNueva')}
              required
              style={{ marginBottom: '1rem' }}
              disabled={cambiandoPassword}
              helperText="Mínimo 6 caracteres"
            />
            <TextField
              fullWidth
              label="Confirmar Nueva Contraseña"
              variant="outlined"
              type="password"
              value={passwordForm.confirmarPassword}
              onChange={handlePasswordFormChange('confirmarPassword')}
              required
              disabled={cambiandoPassword}
              error={passwordForm.confirmarPassword !== '' && passwordForm.passwordNueva !== passwordForm.confirmarPassword}
              helperText={
                passwordForm.confirmarPassword !== '' && passwordForm.passwordNueva !== passwordForm.confirmarPassword
                  ? 'Las contraseñas no coinciden'
                  : ''
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCambiarPassword} disabled={cambiandoPassword}>
            Cancelar
          </Button>
          <Button 
            onClick={handleCambiarPassword} 
            variant="contained"
            color="primary"
            disabled={cambiandoPassword || !isPasswordFormValid()}
            startIcon={cambiandoPassword ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {cambiandoPassword ? 'Cambiando...' : 'Cambiar Contraseña'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TopBar;
